/**
 * Pipeline Spending Adapter
 * Uses projection_timeline.json from map-hcs pipeline when valid.
 * Falls back to spendingCalculatorV2 when pipeline data is missing or implausible.
 */

const PLAUSIBILITY_MIN = 500;   // $B
const PLAUSIBILITY_MAX = 15000; // $B

/**
 * Check if pipeline has valid spending data for a year.
 * @param {Object} projectionTimeline - Parsed projection_timeline.json (or null)
 * @param {number} year
 * @returns {boolean}
 */
export function isPipelineValidForYear(projectionTimeline, year) {
    if (!projectionTimeline?.safecare || !Array.isArray(projectionTimeline.safecare)) {
        return false;
    }
    const entry = projectionTimeline.safecare.find((e) => e.year === year);
    const val = entry?.total_program_cost_billions;
    return val != null && val >= PLAUSIBILITY_MIN && val <= PLAUSIBILITY_MAX;
}

/**
 * Get pipeline spending entry for a year.
 * @param {Object} projectionTimeline
 * @param {number} year
 * @returns {Object|null} Entry or null
 */
export function getPipelineEntryForYear(projectionTimeline, year) {
    if (!projectionTimeline?.safecare) return null;
    return projectionTimeline.safecare.find((e) => e.year === year) || null;
}

/**
 * Apply pipeline spending to a year's spending object from spendingCalculatorV2.
 * Overwrites totalProgramCost and scales breakdown so sum = pipeline total.
 * Uses enrollment_rate from pipeline when available.
 * Optionally scales LTC by ltssMultiplier (config, default 1.0).
 *
 * @param {Object} baseSpending - Result from calculateYearSpending (spendingCalculatorV2)
 * @param {Object} pipelineEntry - Safecare entry from projection_timeline
 * @param {Object} options - { ltssMultiplier: number }
 * @returns {Object} Modified spending object (same shape, pipeline values)
 */
export function applyPipelineSpending(baseSpending, pipelineEntry, options = {}) {
    if (!baseSpending || !pipelineEntry) return baseSpending;

    const ltssMult = options.ltssMultiplier ?? 1.0;
    const acuteBillions = pipelineEntry.total_program_cost_acute_billions ?? 0;
    const ltcBillions = pipelineEntry.ltc_trust_fund_billions ?? 0;
    const scaledLtcBillions = ltcBillions * ltssMult;
    const acuteTotal = acuteBillions * 1e9;
    const ltssTotal = scaledLtcBillions * 1e9;
    const pipelineTotal = acuteTotal + ltssTotal;

    const baseAcute = (baseSpending.totalProgramCost || 0) - (baseSpending.ltss || 0);
    const acuteScale = baseAcute > 0 ? acuteTotal / baseAcute : 1;
    const scaleAcute = (v) => (v != null && typeof v === 'number' ? v * acuteScale : v);

    return {
        ...baseSpending,
        totalProgramCost: pipelineTotal,
        enrollmentRate: pipelineEntry.enrollment_rate ?? baseSpending.enrollmentRate,
        // LTSS from pipeline, scaled by ltssMultiplier
        ltss: ltssTotal,
        // Pipeline LTSS detail for display
        ltssOutlaysHcbsBillions: (pipelineEntry.ltss_outlays_hcbs_billions ?? 0) * ltssMult,
        ltssOutlaysNfBillions: (pipelineEntry.ltss_outlays_nf_billions ?? 0) * ltssMult,
        ltssCopayReceiptsBillions: pipelineEntry.ltss_copay_receipts_billions ?? 0,
        ltssMiContribBillions: pipelineEntry.ltss_mi_contrib_billions ?? 0,
        // Scale acute components so sum = pipeline total
        hospital: scaleAcute(baseSpending.hospital),
        physician: scaleAcute(baseSpending.physician),
        postAcute: scaleAcute(baseSpending.postAcute),
        drug: scaleAcute(baseSpending.drug),
        otherMedical: scaleAcute(baseSpending.otherMedical),
        admin: scaleAcute(baseSpending.admin),
        dental: scaleAcute(baseSpending.dental),
        vision: scaleAcute(baseSpending.vision),
        tuitionSupport: scaleAcute(baseSpending.tuitionSupport),
        loanForgiveness: scaleAcute(baseSpending.loanForgiveness),
        debtRedemption: scaleAcute(baseSpending.debtRedemption),
        safetyNet: scaleAcute(baseSpending.safetyNet),
        capitalGrants: scaleAcute(baseSpending.capitalGrants),
        smallInnovatorSupport: scaleAcute(baseSpending.smallInnovatorSupport),
        extraBehavioralCosts: scaleAcute(baseSpending.extraBehavioralCosts),
        provider: scaleAcute(baseSpending.provider),
        contributionToDashboard: baseSpending.contributionToDashboard
            ? {
                provider: scaleAcute(baseSpending.contributionToDashboard.provider),
                drug: scaleAcute(baseSpending.contributionToDashboard.drug),
                admin: scaleAcute(baseSpending.contributionToDashboard.admin),
                newBenefits: scaleAcute(baseSpending.dental || 0) + scaleAcute(baseSpending.vision || 0) + ltssTotal,
                behavioral: scaleAcute(baseSpending.contributionToDashboard.behavioral)
            }
            : baseSpending.contributionToDashboard
    };
}

/**
 * Get LTSS-specific fields from pipeline for Trust Fund ledger.
 * @param {Object} pipelineEntry
 * @returns {Object} { ltssCopayReceiptsBillions, ltssMiContribBillions, ltssOutlaysHcbsBillions, ltssOutlaysNfBillions }
 */
export function getPipelineLtssFields(pipelineEntry) {
    if (!pipelineEntry) {
        return { ltssCopayReceiptsBillions: 0, ltssMiContribBillions: 0, ltssOutlaysHcbsBillions: 0, ltssOutlaysNfBillions: 0 };
    }
    return {
        ltssCopayReceiptsBillions: pipelineEntry.ltss_copay_receipts_billions ?? 0,
        ltssMiContribBillions: pipelineEntry.ltss_mi_contrib_billions ?? 0,
        ltssOutlaysHcbsBillions: pipelineEntry.ltss_outlays_hcbs_billions ?? 0,
        ltssOutlaysNfBillions: pipelineEntry.ltss_outlays_nf_billions ?? 0
    };
}
